import { z } from 'zod';
import { IdSchema, DateTimeSchema, PaginationParamsSchema, HttpOrInternalFileUrlSchema, } from '../../common';
// ============================================
// Enums
// ============================================
export const GenderSchema = z.enum(['male', 'female']);
export const ApprovalStatusSchema = z.enum(['pending', 'approved', 'rejected']);
// ============================================
// Student — User + StudentProfile composite
// ============================================
export const StudentSchema = z.object({
    // User fields
    id: IdSchema,
    email: z.string().email(),
    name: z.string(),
    phone: z.string().nullable(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    gender: GenderSchema.nullable(),
    avatar: HttpOrInternalFileUrlSchema.nullable(),
    emailVerified: z.boolean(),
    createdAt: DateTimeSchema,
    // StudentProfile fields
    profileId: IdSchema,
    schoolName: z.string().nullable(),
    gradeLevel: z.number().int().min(1).max(12).nullable(),
    classSection: z.string().nullable(),
    lgsPercentile: z.number().nullable(),
    description: z.string().nullable(),
    showDescriptionToParent: z.boolean(),
    approvalStatus: ApprovalStatusSchema,
    /** PDF ödeme: fatura-aktif (grup dönem snapshot'ına dahil). */
    billingActive: z.boolean().default(true),
    educationStartsAt: DateTimeSchema.nullable(),
    educationEndsAt: DateTimeSchema.nullable(),
    /**
     * PDF #1.3 — öğrenci listesinde + profilde "Kalan Süre" gösterimi için
     * server tarafından hesaplanan gün cinsinden kalan süre.
     *  - educationEndsAt null            → null (süresiz)
     *  - geçmiş bir tarih                → 0
     *  - aksi                            → ceil((endsAt - now) / 86_400_000)
     */
    remainingDays: z.number().int().nullable(),
    // Tags (etiketler — Obp, %0-%2, ...)
    tags: z.array(z.string()),
    // Embedded veli özet — admin "öğrenci ekle" panelinde veli adıyla filtre
    // yapabilmek için liste endpoint'inden döndürülür.
    parents: z
        .array(z.object({
        id: IdSchema,
        name: z.string(),
        // W3 (Düzeltme 4) — veli düzenleme formunu ön-doldurmak için e-posta/telefon.
        email: z.string().nullable().optional(),
        phone: z.string().nullable().optional(),
        relationship: z.string().nullable(),
        isPrimary: z.boolean(),
    }))
        .optional()
        .default([]),
    // Aktif kayıtlı eğitimler (liste "Eğitimler" sütunu + profil).
    courses: z
        .array(z.object({ id: IdSchema, name: z.string() }))
        .optional()
        .default([]),
});
// ============================================
// Create / Update
// ============================================
export const CreateStudentSchema = z
    .object({
    // Authentication
    email: z.string().email(),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    // User core
    name: z.string().min(2).max(120),
    phone: z.string().min(7).max(20),
    city: z.string().min(1).max(80).optional(),
    district: z.string().min(1).max(80).optional(),
    gender: GenderSchema.optional(),
    // Student profile
    schoolName: z.string().max(160).optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    classSection: z.string().max(20).optional(),
    lgsPercentile: z.coerce.number().min(0).max(100).optional(),
    description: z.string().max(2000).optional(),
    showDescriptionToParent: z.boolean().optional(),
    // Süreli eğitim
    educationStartsAt: z.string().datetime().optional(),
    educationEndsAt: z.string().datetime().optional(),
    // Veli bilgisi (kayıt sırasında veli oluşturulur ve linke bağlanır)
    parent: z
        .object({
        name: z.string().min(2).max(120),
        phone: z.string().min(7).max(20),
        email: z.string().email(),
        password: z.string().min(8),
        occupation: z.string().max(120).optional(),
        relationship: z.string().max(40).optional(), // örn. "Anne", "Baba"
    })
        .optional(),
    // İlk kayıttaki etiketler
    tags: z.array(z.string().min(1).max(40)).optional(),
    // Kayıt sırasında atanacak eğitimler (mevcut Course id'leri) — bir veya birden
    // fazla. Öğrenci oluşturulduktan sonra CourseEnrollment kayıtları üretilir.
    courseIds: z.array(IdSchema).max(100).optional(),
})
    .superRefine((data, ctx) => {
    // Düzeltme — öğrenci ve veli telefon/e-posta aynı olamaz (görünür hata).
    if (data.parent && data.phone.trim() === data.parent.phone.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['parent', 'phone'],
            message: 'Telefon numarası veya e-mail aynı olamaz',
        });
    }
    if (data.parent && data.email.trim().toLowerCase() === data.parent.email.trim().toLowerCase()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['parent', 'email'],
            message: 'Telefon numarası veya e-mail aynı olamaz',
        });
    }
});
/** Self-registration — parent zorunlu (public form). API endpoint CreateStudentSchema kullanır. */
export const RegisterStudentSchema = CreateStudentSchema.safeExtend({
    parent: z.object({
        name: z.string().min(2).max(120),
        phone: z.string().min(7).max(20),
        email: z.string().email(),
        password: z.string().min(8),
        occupation: z.string().max(120).optional(),
        relationship: z.string().max(40).optional(),
    }),
});
export const UpdateStudentSchema = z.object({
    name: z.string().min(2).max(120).optional(),
    phone: z.string().min(7).max(20).optional(),
    city: z.string().max(80).optional(),
    district: z.string().max(80).optional(),
    gender: GenderSchema.optional(),
    schoolName: z.string().max(160).optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    classSection: z.string().max(20).optional(),
    lgsPercentile: z.coerce.number().min(0).max(100).optional(),
    description: z.string().max(2000).optional(),
    showDescriptionToParent: z.boolean().optional(),
    educationStartsAt: z.string().datetime().nullable().optional(),
    educationEndsAt: z.string().datetime().nullable().optional(),
    // W1 (Düzeltme 4) — yöneticinin öğrenci şifresini sıfırlaması (öğretmendeki gibi).
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number')
        .optional(),
    // W3 (Düzeltme 4) — veli bilgilerini düzenle/oluştur. Var olan birincil veli
    // güncellenir; yoksa ad+e-posta+telefon+şifre verilirse yeni veli oluşturulup
    // bağlanır. Şifre opsiyonel (mevcut veliye yalnızca reset için).
    parent: z
        .object({
        name: z.string().min(2).max(120).optional(),
        phone: z.string().min(7).max(20).optional(),
        email: z.string().email().optional(),
        password: z.string().min(8).optional(),
        occupation: z.string().max(120).optional(),
        relationship: z.string().max(40).optional(),
    })
        .optional(),
});
export const ApproveStudentSchema = z.object({
    status: ApprovalStatusSchema.exclude(['pending']),
    reason: z.string().max(500).optional(),
});
export const SetStudentTagsSchema = z.object({
    tags: z.array(z.string().min(1).max(40)),
});
export const LinkParentSchema = z.object({
    parentUserId: IdSchema,
    relationship: z.string().max(40).optional(),
    isPrimary: z.boolean().optional(),
});
export const StudentQuerySchema = PaginationParamsSchema.extend({
    // Dropdown / picker doldurma icin admin UI 200-500 gibi yuksek limit ister.
    limit: z.coerce.number().int().positive().max(500).default(20),
    search: z.string().optional(),
    approvalStatus: ApprovalStatusSchema.optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    active: z.coerce.boolean().optional(),
    /** Onaylı öğrencilerde fatura aktif/pasif. Aktif = true, pasif = false. */
    billingActive: z.coerce.boolean().optional(),
    // Aldığı eğitime göre filtre (admin öğrenci listesi). Verilen eğitime hâlâ
    // kayıtlı (unenrolledAt = null) öğrencileri döndürür.
    courseId: IdSchema.optional(),
});
// ----------------------------------------------------------------------------
// Eğitim ekle/çıkar (öğrenci detay sayfası — Eğitimler bölümü)
// ----------------------------------------------------------------------------
export const EnrollStudentSchema = z.object({
    courseIds: z.array(IdSchema).min(1).max(100),
});
// ----------------------------------------------------------------------------
// Öğretmen/yönetici notları (PDF: "Kullanıcı Notları") — çoklu, yazarlı,
// opsiyonel veli-görünür. StudentProfile.description (tekil admin notu) ≠ bu.
// ----------------------------------------------------------------------------
export const TeacherStudentNoteSchema = z.object({
    id: IdSchema,
    studentUserId: IdSchema,
    authorUserId: IdSchema,
    authorName: z.string().nullable(),
    body: z.string(),
    visibleToParent: z.boolean(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
});
export const CreateTeacherStudentNoteSchema = z.object({
    body: z.string().min(1).max(4000),
    visibleToParent: z.boolean().optional(),
});
export const UpdateTeacherStudentNoteSchema = z.object({
    body: z.string().min(1).max(4000).optional(),
    visibleToParent: z.boolean().optional(),
});
//# sourceMappingURL=student.js.map